import { Component, OnInit, Input, signal } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, ReactiveFormsModule } from '@angular/forms';
import { faAngleLeft, faPlus, faCaretRight } from '@fortawesome/free-solid-svg-icons';
import { NgbActiveModal, NgbModal, NgbModalRef, NgbTypeaheadSelectItemEvent, NgbTypeahead, NgbTooltip, NgbCollapse } from '@ng-bootstrap/ng-bootstrap';
import dayjs from 'dayjs';
import { Observable, debounceTime, distinctUntilChanged, merge, Subject, switchMap, map } from 'rxjs';
import { AlertService } from '../../../alert.service';
import { Client } from '../../../client';
import { ClientService } from '../../../client.service';
import { NewSiteModalComponent } from '../../../new-site-modal/new-site-modal.component';
import { Site } from '../../../site';
import { SiteService } from '../../../site.service';
import { User } from '../../../user';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { NgIf, NgFor, CommonModule } from '@angular/common';
import { MultiYearAgreementService } from '@app-shared/services/multi-year-agreement.service';
import { Router } from '@angular/router';

@Component({
  selector: 'app-new-multi-year-agreement-modal',
  imports: [CommonModule, ReactiveFormsModule, NgIf, NgbTypeahead, NgFor, NgbTooltip, FaIconComponent],
  templateUrl: './new-multi-year-agreement-modal.component.html',
  styleUrl: './new-multi-year-agreement-modal.component.css'
})
export class NewMultiYearAgreementModalComponent implements OnInit {

  // Modals
  private modalReference: NgbModalRef;

  // Properties
  myaForm: UntypedFormGroup;
  sites: Site[] = [];
  showAddSiteButton: boolean = false;
  optionalStagesAreCollapsed: boolean = true;

  // Clients
  clientTypeaheadInput: (text$: Observable<string>) => Observable<Client[]>;
  clientTypeaheadInputFocus$ = new Subject<string>();
  clientResultFormatter = (client: Client) => client.name;
  selectedClient!: Client;
  myaLengthOptions$ = this.multiYearAgreementService.getMultiYearOptions().pipe(
    map((data: any) =>
      Object.values(data)
        .filter((item: any) => item.isEnabled && item.numOfYears)
        .map((item: any) => item.numOfYears)
    )
  );

  // Font Awesome Properties
  faAngleLeft = faAngleLeft;
  faPlus = faPlus;
  faCaretRight = faCaretRight;
  faCaretDown = faCaretRight

  constructor(private clientService: ClientService,
    private siteService: SiteService,
    private alertService: AlertService,
    private modalService: NgbModal,
    private multiYearAgreementService: MultiYearAgreementService,
    private router: Router,
    public modal: NgbActiveModal) { }

  ngOnInit(): void {
    this.myaForm = new UntypedFormGroup({
      client: new UntypedFormControl(),
      site: new UntypedFormControl(),
      startYear: new UntypedFormControl(dayjs().get('year')),
      myaLength: new UntypedFormControl(),
    });
    this.myaForm.controls.site.disable();
    this.prepareClientTypeahead();
    this.getClients();
  }

  // Open New Site Modal
  openNewSiteModal(): void {
    this.modalReference = this.modalService.open(NewSiteModalComponent);
    this.modalReference.componentInstance.client = this.selectedClient;
    this.modalReference.componentInstance.siteCreated.subscribe(() => {
      this.getSites();
    });
  }

  /* ----- Clients ----- */

  // Get Clients
  private getClients(term?: string): Observable<Client[]> {
    const params = {
      searchTerm: term,
      sortBy: 'client_name',
      sortDirection: 'ASC',
      limit: 10,
      offset: 0
    };
    return this.clientService.getClients(params).pipe(map((res) => { return res.clients; }));
  }

  // Prepare Client Typeahead
  private prepareClientTypeahead(): void {
    this.clientTypeaheadInput = (text$: Observable<string>) => {
      const debouncedText$ = text$.pipe(debounceTime(250), distinctUntilChanged());
      return merge(debouncedText$, this.clientTypeaheadInputFocus$).pipe(switchMap((term) => {
        return this.getClients((term.length == 0) ? null : term);
      }));
    }
  }

  // Select Client
  selectClient(event: NgbTypeaheadSelectItemEvent): void {
    event.preventDefault();
    this.selectedClient = event.item;
    this.myaForm.controls.client.setValue(this.selectedClient.name);
    this.getSites();
  }

  /* ----- Sites ----- */

  // Get Sites
  getSites(): void {
    const params = {
      searchTerm: null,
      sortBy: 'site_name',
      sortDirection: 'ASC',
      limit: null,
      offset: null,
      'filter:client_id': JSON.stringify([this.selectedClient.id])
    };
    this.siteService.getSites(params).subscribe((res) => {
      this.sites = res.sites;
      if (this.sites.length > 0) {
        this.myaForm.controls.site.enable();
        this.myaForm.controls.site.setValue(this.sites[0].id);
        this.showAddSiteButton = false;
      } else {
        this.myaForm.controls.site.disable();
        this.alertService.showWarningAlert('No Sites Available');
        this.showAddSiteButton = true;
      }
    });
  }

  // Add Project
  addNewMya(): void {
    if (this.myaForm.valid) {
      const mya = {
        clientId: this.selectedClient.id,
        siteId: this.myaForm.value.site,
        startYear: this.myaForm.value.startYear,
        length: this.myaForm.value.myaLength,
      };
      this.multiYearAgreementService.addNewMultiYearAgreement(mya).subscribe((myaId) => {
        console.log('Multi-Year Agreement Created:', mya);
        this.modal.close();
        this.alertService.showSuccessAlert('Multi-Year Agreement Created');
        this.router.navigateByUrl(`/multiYearAgreements/${myaId}`);
      });
    } else {
      this.myaForm.markAllAsTouched();
    }
  }

  // Project Form Accessors
  get site() { return this.myaForm.controls.site; }
  get name() { return this.myaForm.controls.name; }
  get startYear() { return this.myaForm.controls.startYear; }
  get myaLength() { return this.myaForm.controls.myaLength; }
}
